import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTagCategories, useDeleteTagCategory } from "@/api/tags";
import { CreateTagCategoryButton } from "./create-tag-category-button";
import { EditTagCategoryForm } from "./edit-tag-category-form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import axios from "axios";
import { TagCategory } from "@/api.types";

interface ErrorResponse {
  error?: string;
}

export function TagCategoriesTable() {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null
  );
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data: categoriesResponse, isLoading } = useTagCategories();
  const deleteTagCategoryMutation = useDeleteTagCategory({
    onSuccess: () => {
      setShowDeleteDialog(false);
      setDeleteError(null);
    },
  });

  // Custom error handler since it's not in the type definition
  const handleDeleteError = (error: unknown) => {
    if (axios.isAxiosError<ErrorResponse>(error)) {
      setDeleteError(
        error.response?.data?.error ??
          "Cannot delete this category. It might have tags associated with it."
      );
    } else {
      setDeleteError("An unexpected error occurred");
    }
  };

  const categories = categoriesResponse?.categories ?? [];
  const selectedCategory =
    categories.find(
      (category: TagCategory) => category.id === selectedCategoryId
    ) ?? null;

  const handleEdit = (category: TagCategory) => {
    setSelectedCategoryId(category.id);
    setShowEditForm(true);
  };

  const handleDelete = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setDeleteError(null);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (selectedCategoryId) {
      deleteTagCategoryMutation.mutate(selectedCategoryId, {
        onError: (error) => {
          handleDeleteError(error);
        },
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium">Tag Categories</h2>
        <CreateTagCategoryButton />
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Tags Count</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-4">
                  Loading categories...
                </TableCell>
              </TableRow>
            ) : categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-4">
                  No categories found. Create your first category.
                </TableCell>
              </TableRow>
            ) : (
              categories.map((category: TagCategory) => (
                <TableRow key={category.id}>
                  <TableCell>{category.name}</TableCell>
                  <TableCell>{category.description ?? "-"}</TableCell>
                  <TableCell>{category.tags?.length ?? 0}</TableCell>
                  <TableCell>
                    <Badge
                      variant={category.isActive ? "default" : "outline"}
                      className={category.isActive ? "bg-green-500" : ""}
                    >
                      {category.isActive ? "ACTIVE" : "INACTIVE"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(category)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(category.id)}
                        disabled={category.tags && category.tags.length > 0}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit Form Dialog */}
      {selectedCategory && (
        <EditTagCategoryForm
          category={selectedCategory}
          open={showEditForm}
          onOpenChange={setShowEditForm}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteError
                ? deleteError
                : "This action cannot be undone. This will permanently delete the category."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {!deleteError && (
              <AlertDialogAction
                onClick={confirmDelete}
                disabled={deleteTagCategoryMutation.isPending}
              >
                {deleteTagCategoryMutation.isPending ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
